import * as React from 'react';

const STORAGE_KEY = 'ai-coaching-review:score-alerts';

const DEFAULT_SCORE_ALERT_THRESHOLD = 85;

export interface ScoreAlertSettings {
	threshold: number;
	/** Off until the browser grants permission, so the grid never polls in the background uninvited. */
	desktopEnabled: boolean;
}

export const DEFAULT_SCORE_ALERT_SETTINGS: ScoreAlertSettings = {
	threshold: DEFAULT_SCORE_ALERT_THRESHOLD,
	desktopEnabled: false,
};

export const clampScoreThreshold = (value: number): number =>
	Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : DEFAULT_SCORE_ALERT_THRESHOLD;

const readSettings = (): ScoreAlertSettings => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return DEFAULT_SCORE_ALERT_SETTINGS;
		}
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') {
			return DEFAULT_SCORE_ALERT_SETTINGS;
		}
		return {
			threshold:
				typeof parsed.threshold === 'number' ? clampScoreThreshold(parsed.threshold) : DEFAULT_SCORE_ALERT_THRESHOLD,
			desktopEnabled: parsed.desktopEnabled === true,
		};
	} catch {
		return DEFAULT_SCORE_ALERT_SETTINGS;
	}
};

export function useScoreAlertSettings() {
	const [settings, setSettingsState] = React.useState<ScoreAlertSettings>(readSettings);

	const setSettings = React.useCallback((next: ScoreAlertSettings) => {
		const sanitized: ScoreAlertSettings = { ...next, threshold: clampScoreThreshold(next.threshold) };
		setSettingsState(sanitized);
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
		} catch {
			// Ignore write failures (private mode / storage disabled) — in-memory state still updates.
		}
	}, []);

	return [settings, setSettings] as const;
}
